import { Input } from "@synthesis/shared-ui";

import { createApplicationApplicantInputMasks } from "../../create-application-applicant-input-masks";
import { getApplicantTypeOptionsExcludingIndividual } from "../../create-application-dictionaries.lib";
import { useCreateApplicationDictionariesContext } from "../../create-application-dictionaries-context";
import { isIndividualApplicantType, type ApplicantType } from "../../create-application-page.lib";
import { ApplicantRadioGroup } from "./create-application-applicant-form-controls";
import { CreateApplicationFormField, CreateApplicationFormRow, CreateApplicationFormSection } from "../create-application-form-layout";

type ApplicantRegistrationSectionProps = {
  applicantType: ApplicantType;
  namePrefix?: "payer";
  title?: string;
};

export const ApplicantRegistrationSection = ({
  applicantType,
  namePrefix,
  title = "Регистрационные реквизиты",
}: ApplicantRegistrationSectionProps) => {
  const { dictionaries } = useCreateApplicationDictionariesContext();
  const isPayer = namePrefix === "payer";
  const isIndividual = isIndividualApplicantType(applicantType);
  const isIndividualEntrepreneur = applicantType === "SOLE_PROPRIETOR";
  const applicantTypeOptions = isPayer ? getApplicantTypeOptionsExcludingIndividual(dictionaries.applicantTypes) : dictionaries.applicantTypes;
  const field = (applicantName: string, payerName: string) => (isPayer ? payerName : applicantName);

  return (
    <CreateApplicationFormSection title={title} required>
      <CreateApplicationFormField label="Тип заявителя / лица" required>
        <ApplicantRadioGroup name={isPayer ? "payerApplicantType" : "applicantType"} options={applicantTypeOptions} horizontal />
      </CreateApplicationFormField>

      <CreateApplicationFormField label={isIndividual ? "Полное ФИО" : "Полное наименование / ИП ФИО"} required>
        <Input
          name={field("organization", "payerOrganization")}
          placeholder={isIndividual ? "Иванов Иван Иванович" : "Общество с ограниченной ответственностью «_____» / ИП Иванов Иван Иванович"}
        />
      </CreateApplicationFormField>

      {isIndividual || isIndividualEntrepreneur ? (
        <CreateApplicationFormRow>
          {!isPayer && (
            <CreateApplicationFormField label="СНИЛС">
              <Input name="snils" placeholder="XXX-XXX-XXX XX" settings={createApplicationApplicantInputMasks.snils} />
            </CreateApplicationFormField>
          )}
          <CreateApplicationFormField label="ИНН" required>
            <Input name={field("inn", "payerInn")} placeholder="(10 или 12 цифр)" settings={createApplicationApplicantInputMasks.inn} />
          </CreateApplicationFormField>
          {isIndividualEntrepreneur && (
            <CreateApplicationFormField label="ОГРН / ОГРНИП" required className="lg:max-w-[calc(50%-0.5rem)]">
              <Input name={field("ogrn", "payerOgrn")} placeholder="(13 или 15 цифр)" settings={createApplicationApplicantInputMasks.ogrn} />
            </CreateApplicationFormField>
          )}
        </CreateApplicationFormRow>
      ) : (
        <>
          <CreateApplicationFormRow>
            <CreateApplicationFormField label="Сокращённое наименование" required>
              <Input name={field("shortOrganizationName", "payerShortOrganizationName")} placeholder="ООО «_____»" />
            </CreateApplicationFormField>
            <CreateApplicationFormField label="ИНН" required>
              <Input name={field("inn", "payerInn")} placeholder="(10 или 12 цифр)" settings={createApplicationApplicantInputMasks.inn} />
            </CreateApplicationFormField>
          </CreateApplicationFormRow>

          <CreateApplicationFormRow>
            <CreateApplicationFormField label="ОГРН / ОГРНИП" required>
              <Input name={field("ogrn", "payerOgrn")} placeholder="(13 или 15 цифр)" settings={createApplicationApplicantInputMasks.ogrn} />
            </CreateApplicationFormField>
            <CreateApplicationFormField label="ОКВЭД">
              <Input name={field("okved", "payerOkved")} placeholder="47.11, 01.11, 49.41" />
            </CreateApplicationFormField>
          </CreateApplicationFormRow>

          <CreateApplicationFormRow>
            <CreateApplicationFormField label="ОКТМО / ОКАТО" hint="Укажите код ОКТМО (или ОКАТО при наличии)">
              <Input
                name={field("oktmoOkato", "payerOktmoOkato")}
                placeholder="(8 или 10 цифр)"
                settings={createApplicationApplicantInputMasks.oktmoOkato}
              />
            </CreateApplicationFormField>
            <CreateApplicationFormField label="ОКПО">
              <Input name={field("okpo", "payerOkpo")} placeholder="(8 или 10 цифр)" settings={createApplicationApplicantInputMasks.okpo} />
            </CreateApplicationFormField>
          </CreateApplicationFormRow>

          <CreateApplicationFormRow>
            <CreateApplicationFormField label="КПП">
              <Input name={field("kpp", "payerKpp")} placeholder="(9 цифр)" settings={createApplicationApplicantInputMasks.kpp} />
            </CreateApplicationFormField>
          </CreateApplicationFormRow>
        </>
      )}
    </CreateApplicationFormSection>
  );
};
